import { ref, computed } from 'vue'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { Subscriptions_PlansResponseDados } from '../contracts/Subscriptions_PlansResponse'
import type { Subscriptions_PlanInput } from '../contracts/Subscriptions_PlanInput'
import type { Subscriptions_PlanRow } from '../contracts/Subscriptions_PlanRow'
import type { Subscriptions_ExtendInput } from '../contracts/Subscriptions_ExtendInput'
import type { Subscriptions_ExtendResponseDados } from '../contracts/Subscriptions_ExtendResponse'
import type { Subscriptions_AdminListResponseDados } from '../contracts/Subscriptions_AdminListResponse'
import type { Subscriptions_AdminSubscriptionRow } from '../contracts/Subscriptions_AdminSubscriptionRow'
import type { Subscriptions_AdminCancelInput } from '../contracts/Subscriptions_AdminCancelInput'
import type { Subscriptions_AdminCancelResponseDados } from '../contracts/Subscriptions_AdminCancelResponse'
import type { Subscriptions_PlanPriceInput } from '../contracts/Subscriptions_PlanPriceInput'
import type { Subscriptions_PlanActiveInput } from '../contracts/Subscriptions_PlanActiveInput'
import type { Subscriptions_AdminSweepRunResponseDados } from '../contracts/Subscriptions_AdminSweepRunResponse'
import type { Subscriptions_AdminSubscriptionProcessResponseDados } from '../contracts/Subscriptions_AdminSubscriptionProcessResponse'
import type { Subscriptions_AdminSweepRunsResponseDados } from '../contracts/Subscriptions_AdminSweepRunsResponse'
import type { Subscriptions_AdminSweepRunRow } from '../contracts/Subscriptions_AdminSweepRunRow'
// Import side-effect: registers the contracts with the responseContract registry.
import '../contracts/Subscriptions_PlansResponse'
import '../contracts/Subscriptions_PlanRow'
import '../contracts/Subscriptions_PlanInput'
import '../contracts/Subscriptions_PlanPriceInput'
import '../contracts/Subscriptions_PlanActiveInput'
import '../contracts/Subscriptions_ExtendInput'
import '../contracts/Subscriptions_ExtendResponse'
import '../contracts/Subscriptions_AdminListResponse'
import '../contracts/Subscriptions_AdminSubscriptionRow'
import '../contracts/Subscriptions_AdminCancelInput'
import '../contracts/Subscriptions_AdminCancelResponse'
import '../contracts/Subscriptions_AdminSweepRunInput'
import '../contracts/Subscriptions_AdminSweepRunResponse'
import '../contracts/Subscriptions_AdminSubscriptionProcessInput'
import '../contracts/Subscriptions_AdminSubscriptionProcessResponse'
import '../contracts/Subscriptions_AdminSweepRunOutcomeRow'
import '../contracts/Subscriptions_AdminSweepRunRow'
import '../contracts/Subscriptions_AdminSweepRunsResponse'

/**
 * A single market (price row) within a plan group.
 *
 * provider_code: the gateway that will charge this price row (e.g. 'stripe', 'asaas', 'pagarme').
 * countries: ISO 3166-1 alpha-2 codes the price row is offered in (e.g. ['BR', 'US']).
 * provider_price_ref: Stripe Price ID (only present when provider_code === 'stripe').
 */
export interface PlanMarket {
  currency: string
  price_cents: number
  provider_code: string
  countries: string[]
  provider_price_ref?: string
  /** @deprecated alias kept for existing list-rendering; maps to provider_price_ref */
  stripe_price_id?: string
}

/**
 * A grouped plan — one entry per unique plan id, with all of its
 * market (price-row) records aggregated into `markets`.
 *
 * Market order: BRL first, then remaining currencies sorted alphabetically.
 */
export interface PlanGroup {
  id: number
  name: string
  cycle: string
  monthly_credit_grant: number
  active: number
  markets: PlanMarket[]
}

/** Fields used when saving plan-only changes (no price rows touched). */
export interface PlanFieldsInput {
  name: string
  monthly_credit_grant: number
  cycle: string
  active: number
}

/**
 * useSubscriptionAdmin — SOLE network owner for subscriptions/* admin routes.
 *
 * All requests go to `config.api.sistemaHost` (app-api-service).
 * The `no_httpservice_in_vue` qa rule forbids any fetch in .vue files —
 * this composable is the single gateway.
 *
 * Call `_reset()` in afterEach for test isolation.
 */

// ── Module-scope singleton state ────────────────────────────────────────────

const plans = ref<Subscriptions_PlanRow[]>([])
const adminRows = ref<Subscriptions_AdminSubscriptionRow[]>([])

/**
 * groupedPlans — one PlanGroup per unique plan id, derived from `plans`.
 *
 * Each plan's markets are sorted: BRL first, then remaining currencies
 * in alphabetical order.
 */
const groupedPlans = computed<PlanGroup[]>(() => {
  const map = new Map<number, PlanGroup>()
  for (const row of plans.value) {
    if (!map.has(row.id)) {
      map.set(row.id, {
        id: row.id,
        name: row.name,
        cycle: row.cycle,
        monthly_credit_grant: row.monthly_credit_grant,
        active: row.active,
        markets: [],
      })
    }
    // Only push a market when BOTH currency and price_cents are present.
    // A market-less plan arrives with currency===null / price_cents===null
    // (LEFT JOIN in the backend); skip those rows to avoid a phantom market.
    if (row.currency != null && row.price_cents != null) {
      const group = map.get(row.id)!
      group.markets.push({
        currency: row.currency,
        price_cents: row.price_cents,
        provider_code: row.provider_code ?? '',
        countries: row.countries ?? [],
        ...(row.provider_price_ref != null ? { provider_price_ref: row.provider_price_ref } : {}),
        ...(row.stripe_price_id != null ? { stripe_price_id: row.stripe_price_id } : {}),
      })
    }
  }
  for (const group of map.values()) {
    group.markets.sort((a, b) => {
      if (a.currency === 'BRL') return -1
      if (b.currency === 'BRL') return 1
      return a.currency.localeCompare(b.currency)
    })
  }
  return Array.from(map.values())
})

// ── Internal helpers ─────────────────────────────────────────────────────────

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

// ── Public composable ────────────────────────────────────────────────────────

export function useSubscriptionAdmin() {
  /**
   * listPlans — GET subscriptions/plans.
   *
   * D3: `opts.includeInactive` appends `?include_inactive=1` so retired
   * (active=0) plans appear in the staff list and can be reactivated.
   * Default keeps the active-only behavior.
   */
  async function listPlans(opts?: { includeInactive?: boolean }): Promise<void> {
    const params = opts?.includeInactive === true ? '?include_inactive=1' : ''
    try {
      const raw = await httpService.get(
        sistemaUrl(`subscriptions/plans${params}`),
        { responseContract: 'Subscriptions_PlansResponse', skipErrorDisplay: false },
      )
      const resp = raw as { dados: Subscriptions_PlansResponseDados }
      plans.value = resp.dados.rows
      log('useSubscriptionAdmin', 'listPlans', 'Planos carregados', { count: plans.value.length, includeInactive: opts?.includeInactive === true })
    } catch (err) {
      log('useSubscriptionAdmin', 'listPlans', 'Erro ao carregar planos', {
        message: (err as Error)?.message,
        status: (err as HttpError)?.status,
      })
      throw err
    }
  }

  /**
   * createPlan — POST subscriptions/plans/create.
   * Plan-only payload: name, cycle, monthly_credit_grant, active.
   * No price_cents / currency / stripe_price_id — those are added via saveMarket.
   * Returns the new plan id (from resp.dados.rows[0].id).
   */
  async function createPlan(input: Pick<Subscriptions_PlanInput, 'name' | 'cycle' | 'monthly_credit_grant' | 'active'>): Promise<number> {
    const body: Pick<Subscriptions_PlanInput, 'name' | 'cycle' | 'monthly_credit_grant' | 'active'> = {
      name: input.name,
      cycle: input.cycle,
      monthly_credit_grant: input.monthly_credit_grant,
      active: input.active,
    }
    const raw = await httpService.post(
      sistemaUrl('subscriptions/plans/create'),
      body,
      {
        inputContract: 'Subscriptions_PlanInput',
        responseContract: 'Subscriptions_PlansResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'create_plan_failed',
      },
    )
    const resp = raw as { dados: Subscriptions_PlansResponseDados }
    const newId = resp.dados.rows[0].id
    log('useSubscriptionAdmin', 'createPlan', 'Plano criado', { name: input.name, id: newId })
    return newId
  }

  /**
   * listAll — GET subscriptions/admin/list.
   * Returns all franchise subscriptions (live + history) for staff admin.
   */
  async function listAll(): Promise<void> {
    try {
      const raw = await httpService.get(
        sistemaUrl('subscriptions/admin/list'),
        { responseContract: 'Subscriptions_AdminListResponse', skipErrorDisplay: false },
      )
      const resp = raw as { dados: Subscriptions_AdminListResponseDados }
      adminRows.value = resp.dados.rows
      log('useSubscriptionAdmin', 'listAll', 'Assinaturas carregadas', { count: adminRows.value.length })
    } catch (err) {
      log('useSubscriptionAdmin', 'listAll', 'Erro ao carregar assinaturas', {
        message: (err as Error)?.message,
        status: (err as HttpError)?.status,
      })
      throw err
    }
  }

  /**
   * extend — POST subscriptions/extend.
   * Binds EXACTLY the Subscriptions_ExtendInput field names: exactly one of
   * subscription_id (extend an existing subscription) or franquia_id (grant a
   * grace window to a franchise WITHOUT a subscription) plus days.
   *
   * M1: a backend refusal (e.g. 'live_subscription_exists', 'subscription_not_found')
   * comes back as HTTP 200 with the ENVELOPE's `success:false` (sendError() never
   * touches the HTTP status — MY_Controller.php:291-299), so the envelope is the
   * only reliable refusal signal. `dados.success` cannot be used instead: it is a
   * redundant field the backend now writes as a hardcoded `true` on the success path,
   * and existing contract-derived test fixtures default it to `false`. R6': the
   * check itself now lives in httpService (`throwOnEnvelopeFailure`), so it can
   * no longer be forgotten by the next write on this route.
   */
  async function extend(input: Subscriptions_ExtendInput): Promise<Subscriptions_ExtendResponseDados> {
    const body: Subscriptions_ExtendInput = {
      days: input.days,
      ...(input.subscription_id != null ? { subscription_id: input.subscription_id } : {}),
      ...(input.franquia_id != null ? { franquia_id: input.franquia_id } : {}),
    }
    const raw = await httpService.post(
      sistemaUrl('subscriptions/extend'),
      body,
      {
        inputContract: 'Subscriptions_ExtendInput',
        responseContract: 'Subscriptions_ExtendResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'extend_failed',
      },
    )
    const resp = raw as { dados: Subscriptions_ExtendResponseDados }
    log('useSubscriptionAdmin', 'extend', 'Assinatura estendida', {
      subscription_id: input.subscription_id,
      franquia_id: input.franquia_id,
      days: input.days,
      new_grace_until: resp.dados?.new_grace_until,
    })
    return resp.dados
  }

  /**
   * cancelSubscription — POST subscriptions/admin/cancel (S12/M3d).
   *
   * The staff console's manual cut. TERMINAL by decision (D2): there is no
   * reactivation, the franchise re-subscribes from scratch.
   *
   * REFUSALS ARRIVE AS HTTP 200. sendError() never touches the HTTP status
   * (MY_Controller.php), so httpService does not throw and validateResponse()
   * does not even run — the ENVELOPE's `success:false` is the only refusal
   * signal, exactly as in extend() above. Reading `dados.success` instead would
   * report every refusal as a completed cancellation.
   *
   * The rejection carries the backend's own machine reason so the caller can
   * choose its tone: `retry_scheduled` means the gateway still has a payment
   * attempt pending and the cut was DEFERRED — the system working correctly,
   * not a failure. Everything else (`subscription_not_cancellable`,
   * `subscription_not_found`, `gateway_not_registered`, `gateway_cannot_cancel`,
   * `gateway_cancel_failed: …`, `local_write_failed: …`) is a real refusal or
   * error. In every one of those cases the subscription was NOT cancelled.
   */
  async function cancelSubscription(subscriptionId: number): Promise<Subscriptions_AdminCancelResponseDados> {
    const body: Subscriptions_AdminCancelInput = { subscription_id: subscriptionId }
    const raw = await httpService.post(
      sistemaUrl('subscriptions/admin/cancel'),
      body,
      {
        inputContract: 'Subscriptions_AdminCancelInput',
        responseContract: 'Subscriptions_AdminCancelResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'cancel_failed',
      },
    )
    const resp = raw as { dados: Subscriptions_AdminCancelResponseDados }
    log('useSubscriptionAdmin', 'cancelSubscription', 'Assinatura cancelada', {
      subscription_id: subscriptionId,
      previous_status: resp.dados?.previous_status,
      remote: resp.dados?.remote,
    })
    return resp.dados
  }

  /**
   * runSweep — POST subscriptions/admin/sweep/run (S12/#17a).
   *
   * Runs the WHOLE carência round on demand, the same round the scheduler runs.
   * It takes no parameters by design: the clock and the cut policy belong to the
   * mechanism (see Subscriptions_AdminSweepRunInput).
   *
   * The RESULT of a round is never a refusal — every counter it returns, including
   * `circuit_open: true`, arrives in a success envelope because the staff asked
   * the mechanism what it did. Only the Full gate and an unexpected exception
   * leave through sendError(), and those still answer HTTP 200 (MY_Controller.php),
   * so the ENVELOPE's `success:false` is the only signal that the round never ran.
   */
  async function runSweep(): Promise<Subscriptions_AdminSweepRunResponseDados> {
    const raw = await httpService.post(
      sistemaUrl('subscriptions/admin/sweep/run'),
      {},
      {
        inputContract: 'Subscriptions_AdminSweepRunInput',
        responseContract: 'Subscriptions_AdminSweepRunResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'sweep_failed',
      },
    )
    const resp = raw as { dados: Subscriptions_AdminSweepRunResponseDados }
    log('useSubscriptionAdmin', 'runSweep', 'Rodada executada', {
      candidate_count: resp.dados?.candidate_count,
      cancelled_count: resp.dados?.cancelled_count,
      circuit_open: resp.dados?.circuit_open,
    })
    return resp.dados
  }

  /**
   * processSubscription — POST subscriptions/admin/subscriptions/{id}/process (S12/#17a).
   *
   * The round aimed at ONE subscription. Unlike cancelSubscription, this endpoint
   * is a QUESTION ("what does the sweep do with this row today?"), so EVERY
   * outcome — including `not_due`, `deferred` and `gateway_failed` — comes back in
   * a success envelope with the outcome in `dados.outcome`. The caller reads the
   * outcome to choose its tone; it must NOT treat a non-cancelled outcome as an
   * error. Only auth, an unknown id and an exception arrive as `success:false`
   * (again on HTTP 200).
   */
  async function processSubscription(subscriptionId: number): Promise<Subscriptions_AdminSubscriptionProcessResponseDados> {
    const raw = await httpService.post(
      sistemaUrl(`subscriptions/admin/subscriptions/${subscriptionId}/process`),
      {},
      {
        inputContract: 'Subscriptions_AdminSubscriptionProcessInput',
        responseContract: 'Subscriptions_AdminSubscriptionProcessResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'process_failed',
      },
    )
    const resp = raw as { dados: Subscriptions_AdminSubscriptionProcessResponseDados }
    log('useSubscriptionAdmin', 'processSubscription', 'Assinatura processada', {
      subscription_id: subscriptionId,
      outcome: resp.dados?.outcome,
      status: resp.dados?.status,
    })
    return resp.dados
  }

  /**
   * fetchSweepRuns — GET subscriptions/admin/sweep/runs (S12/#17a run log).
   *
   * The round's diary: every recorded INVOCATION of the sweep, newest first,
   * whichever door fired it. Read-only — every row here already happened.
   *
   * The rows are RETURNED, never parked in module state: the log is read on
   * demand and a cached copy would hide the round the staff just triggered.
   *
   * Refusals (the Full gate) arrive on HTTP 200 with the ENVELOPE's
   * `success:false` — sendError() never touches the HTTP status, so httpService
   * does not throw and an unchecked caller would render an empty-but-happy table
   * for a log it was never allowed to read.
   */
  async function fetchSweepRuns(): Promise<Subscriptions_AdminSweepRunRow[]> {
    const raw = await httpService.get(
      sistemaUrl('subscriptions/admin/sweep/runs'),
      {
        responseContract: 'Subscriptions_AdminSweepRunsResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'sweep_runs_failed',
      },
    )
    const resp = raw as { dados: Subscriptions_AdminSweepRunsResponseDados }
    const rows = resp.dados?.rows ?? []
    log('useSubscriptionAdmin', 'fetchSweepRuns', 'Histórico de rodadas carregado', { count: rows.length })
    return rows
  }

  /**
   * savePlanFields — PUT subscriptions/plans/{id} with ONLY plan fields.
   * No price_cents or currency — the backend leaves all price rows untouched.
   * A grant/cycle change will still version the plan server-side.
   *
   * D1: the backend responds with the rows of the plan that is NOW live —
   * on a versioning save that is a NEW plan id (the old one is superseded).
   * Returns that effective id so callers follow the new version; on a
   * cosmetic save rows[0].id === planId (uniform).
   */
  async function savePlanFields(planId: number, fields: PlanFieldsInput): Promise<number> {
    const body: Subscriptions_PlanInput = {
      name: fields.name,
      cycle: fields.cycle,
      monthly_credit_grant: fields.monthly_credit_grant,
      active: fields.active,
    }
    const raw = await httpService.put(
      sistemaUrl(`subscriptions/plans/${planId}`),
      body,
      {
        inputContract: 'Subscriptions_PlanInput',
        responseContract: 'Subscriptions_PlansResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'save_plan_failed',
      },
    )
    const resp = raw as { dados: Subscriptions_PlansResponseDados }
    const effectiveId = resp.dados.rows[0].id
    log('useSubscriptionAdmin', 'savePlanFields', 'Campos do plano salvos', { planId, effectiveId, name: fields.name })
    return effectiveId
  }

  /**
   * setPlanActive — POST subscriptions/plans/{id}/active.
   *
   * D3: flips a plan's active flag (retire/reactivate) mirroring the credit
   * packs set_active action. Superseded plan versions are refused server-side
   * with 'plan_version_superseded'.
   */
  async function setPlanActive(planId: number, active: 0 | 1): Promise<void> {
    const body: Subscriptions_PlanActiveInput = { active }
    await httpService.post(
      sistemaUrl(`subscriptions/plans/${planId}/active`),
      body,
      {
        inputContract: 'Subscriptions_PlanActiveInput',
        responseContract: 'Subscriptions_PlansResponse',
        skipErrorDisplay: false,
        // 'plan_version_superseded' volta em HTTP 200: sem isto a linha pisca
        // para o novo estado e a tabela recarrega mostrando o estado antigo.
        throwOnEnvelopeFailure: 'plan_active_failed',
      },
    )
    log('useSubscriptionAdmin', 'setPlanActive', 'Plano ativado/desativado', { planId, active })
  }

  /**
   * saveMarket — POST subscriptions/plans/{plan.id}/prices with the market fields.
   *
   * Upserts one live price row for a (plan, currency, provider_code) tuple.
   * provider_price_ref is included only when provider_code === 'stripe'.
   */
  async function saveMarket(plan: PlanGroup, market: PlanMarket): Promise<void> {
    const body: Subscriptions_PlanPriceInput = {
      currency: market.currency,
      provider_code: market.provider_code,
      price_cents: market.price_cents,
      countries: market.countries,
      ...(market.provider_price_ref != null && market.provider_price_ref !== ''
        ? { provider_price_ref: market.provider_price_ref }
        : {}),
    }
    await httpService.post(
      sistemaUrl(`subscriptions/plans/${plan.id}/prices`),
      body,
      {
        inputContract: 'Subscriptions_PlanPriceInput',
        responseContract: 'Subscriptions_PlansResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'plan_price_failed',
      },
    )
    log('useSubscriptionAdmin', 'saveMarket', 'Mercado salvo', { planId: plan.id, currency: market.currency, provider_code: market.provider_code })
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    plans.value = []
    adminRows.value = []
  }

  return {
    // reactive state
    plans,
    adminRows,
    groupedPlans,
    // methods
    listPlans,
    createPlan,
    savePlanFields,
    setPlanActive,
    saveMarket,
    listAll,
    extend,
    cancelSubscription,
    runSweep,
    fetchSweepRuns,
    processSubscription,
    _reset,
  }
}
